<template>
    <div class="reports profiles-with-navigation no-print">
        <ReportSideBar
            :id="id"
            :reports-list="defaultReports"
            :custom-reports-list="customReports"
            :collapsed="false"
            :remove-entry="deleteReport"
            :report-mode="mode"
        />
        <div class="component-with-nav-body">
            <div class="common-form-wrapper">
                <div v-if="notAvailableReport">
                    <h4>{{ $t('reports.notAvailable') }}</h4>
                </div>
                <form v-else>
                    <h4 v-if="mode === 'add'">{{ reportData.name }}</h4>
                    <div v-if="mode === 'edit'" class="wrapper">
                        <span v-if="$v.reportData.name.$invalid" class="tooltiptext">{{
                            errorDisplay($v.reportData.name, { val: MAX_REPORT_NAME_LENGTH })
                        }}</span>
                        <div
                            class="input-with-insidelabel"
                            :class="{ tooltiperror: $v.reportData.name.$invalid }"
                        >
                            <input id="listenIn" v-model="reportData.name" class="time-input" type="text" />
                            <label for="listenIn">{{ $t('common.name') }}</label>
                        </div>
                    </div>
                    <div v-if="mode === 'edit'" class="wrapper-text">
                        <span>{{ $t('reports.labels.type') }}:</span><span>{{ reportData.typeName }}</span>
                    </div>
                    <div v-if="userType === USER_TYPES.SYS_ADMIN" class="multiselect-wrapper">
                        <Multiselect
                            ref="assignmentAreasMultiselect"
                            :placeholder="
                                reportData.aa.length
                                    ? $t('reporting.commonLabels.aAreas')
                                    : $t('reporting.commonLabels.allAA')
                            "
                            :multiple="true"
                            :options="aAreasOptions"
                            :limit="5"
                            label="name"
                            track-by="name"
                            open-direction="bottom"
                            :value="reportData.aa"
                            :select-label="emptyLabel"
                            :deselect-label="emptyLabel"
                            :selected-label="emptyLabel"
                            :close-on-select="false"
                            @input="updateAAandLocations"
                        >
                            <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
                        </Multiselect>
                    </div>
                    <div class="multiselect-wrapper">
                        <Multiselect
                            ref="locationsMultiselect"
                            v-model="reportData.locations"
                            :placeholder="
                                reportData.locations.length
                                    ? $t('reporting.commonLabels.locations')
                                    : $t('reporting.commonLabels.allLocations')
                            "
                            :multiple="true"
                            :options="locationsOptions"
                            :limit="10"
                            label="name"
                            track-by="name"
                            open-direction="bottom"
                            :select-label="emptyLabel"
                            :deselect-label="emptyLabel"
                            :selected-label="emptyLabel"
                            :close-on-select="false"
                        >
                            <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
                        </Multiselect>
                    </div>
                    <div v-if="reportData.type === REPORT_TYPES.STAFF_PERFORMANCE" class="wrapper">
                        <span v-if="$v.reportData.callId.$invalid" class="tooltiptext">{{
                            errorDisplay($v.reportData.callId)
                        }}</span>
                        <SelectBox
                            v-model="reportData.callId"
                            :value="reportData.callId"
                            :options="callIdsOptions"
                            :placeholder="$t('reports.labels.callId')"
                            :invalid="$v.reportData.callId.$invalid"
                        />
                    </div>
                    <div v-if="reportData.type !== REPORT_TYPES.EXCEPTION" class="multiselect-wrapper">
                        <Multiselect
                            ref="callTypesMultiselect"
                            v-model="reportData.callTypes"
                            :placeholder="
                                reportData.callTypes.length
                                    ? $t('reporting.commonLabels.callTypes')
                                    : $t('reporting.commonLabels.allCallTypes')
                            "
                            :multiple="true"
                            :options="callTypesOptions"
                            label="name"
                            track-by="name"
                            open-direction="bottom"
                            :select-label="emptyLabel"
                            :deselect-label="emptyLabel"
                            :selected-label="emptyLabel"
                            :close-on-select="false"
                        >
                            <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
                        </Multiselect>
                    </div>
                    <div v-else class="wrapper">
                        <SelectBox
                            v-model="reportData.callType"
                            :options="callTypesOptions"
                            :placeholder="$t('reporting.commonLabels.callType')"
                        />
                    </div>
                    <div v-if="reportData.type === REPORT_TYPES.PAIR_EVENT" class="wrapper">
                        <SelectBox
                            v-model="reportData.pairEvent"
                            :options="EVENT_PAIR_TYPES"
                            :placeholder="$t('reports.labels.eventPair')"
                        />
                    </div>
                    <DateTimePickerForm ref="DateTimePickerForm" v-model="reportData.exportDate" />
                </form>
            </div>
            <EventsTable
                v-if="reportData.type === REPORT_TYPES.ASCOM_EVENT"
                v-model="reportData.selectedEvent"
                :report-data="reportData"
                :mode="mode"
            />
            <Scheduler
                v-if="mode === 'edit' && reportData.type !== REPORT_TYPES.ASCOM_EVENT"
                :schedule="reportData.schedule"
                :is-period-time-inline="true"
                class="common-form-wrapper"
                @update="updateSchedule"
            />
            <span v-if="mode === 'edit' && reportData.type === REPORT_TYPES.ASCOM_EVENT" class="bold-text">{{
                $t('reports.scheduleNotCreated')
            }}</span>
        </div>
    </div>
</template>

<script type="text/babel">
import ReportSideBar from './ReportSideBar.vue';
import Multiselect from 'moxa-vue-multiselect';
import SelectBox from '../common/SelectBox.vue';
import mappingState from '../common/mappingState';
import reportingUtils, {
    saveReport,
    getCustomReportObject,
    getSelectedAAreas,
    toArray,
    toNumberArray,
} from '../common/reportingUtils';
import DateTimePickerForm from '../common/datetimepicker/DateTimePickerForm.vue';
import { mapState, mapMutations } from 'vuex';
import isEmpty from 'lodash-es/isEmpty';
import invoke from 'lodash-es/invoke';
import orderBy from 'lodash-es/orderBy';
import transform from 'lodash-es/transform';
import uniqBy from 'lodash-es/uniqBy';
import {
    minimumLength,
    maximumReportNameLength,
    forbiddenNames,
    errorDisplay,
    isNotEmptyCallId,
} from '../../validators';

import {
    SUCCESS,
    ERROR,
    MAX_REPORT_NAME_LENGTH,
    REPORT_TYPES,
    EVENT_PAIR_TYPES,
    ROUTE_NAMES,
    USER_TYPES,
} from '../../constants';

import EventsTable from './helpers/EventsTable.vue';
import Scheduler from './Scheduler.vue';
import { resourceCustomReport, resourceAssignmentAreas } from '../../api/resources';
import set from 'date-fns/set';
import add from 'date-fns/add';
import { errorHandler, sortLocations } from '../common/helpers';

let currentDate = set(new Date(), { hours: 0, minutes: 0, seconds: 0 });
let nextDate = add(currentDate, { days: 1 });

export default {
    components: {
        Multiselect,
        SelectBox,
        ReportSideBar,
        DateTimePickerForm,
        EventsTable,
        Scheduler,
    },

    mixins: [mappingState, reportingUtils],

    constants: {
        MAX_REPORT_NAME_LENGTH,
        REPORT_TYPES,
        EVENT_PAIR_TYPES,
        USER_TYPES,
    },

    props: {
        id: {
            type: Number,
            required: true,
        },

        mode: {
            type: String,
            required: true,
        },

        queryData: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        defaultReports: {
            type: Array,
            required: true,
        },

        customReports: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            reportData: {
                id: null,
                name: '',
                type: 0,
                typeName: '',
                aa: [],
                locations: [],
                callTypes: [],
                exportDate: {
                    from: {
                        date: currentDate,
                    },
                    to: {
                        date: nextDate,
                    },
                    invalid: false,
                },
                pairEvent: 6,
                callType: 1,
                callId: '',
                selectedEvent: {},
                scheduleValid: true,
                schedule: {
                    scheduled: false,
                    scheduleType: 7,
                    emailingTime: '00:00',
                    scheduleEmails: this.defaultRecipients,
                    scheduleDescription: '',
                    scheduleLastRun: null,
                },
            },
            emptyLabel: '',
            custom: null,
            unwatcher: null,
            reportCount: 0,
        };
    },

    validations() {
        return {
            reportData: {
                name: {
                    minimumLength,
                    maximumReportNameLength,
                    forbiddenNames: val => forbiddenNames(val, this.getForbiddenReportNames),
                },

                scheduleValid: {
                    isTrue: val => val,
                },

                callId: { isNotEmptyCallId },
            },
        };
    },

    computed: {
        ...mapState({
            aAreas: 'assignmentArea',
            callTypes: 'callTypes',
            unsavedChanges: 'unsavedChanges',
            staff: 'staff',
            user: state => state.user.user,
            defaultRecipients: state => state.defaultRecipients,
        }),

        /**
         * Checks the report for availability, depending on the type of user and report
         */
        notAvailableReport() {
            return this.user.type === 4 && this.reportData.type === REPORT_TYPES.STAFF_PERFORMANCE;
        },

        /**
         * Returns list of staff for selected aa
         */
        selectedStaff() {
            let staff = [];

            this.selectedAA.forEach(aa => {
                this.staff.forEach(st => {
                    st.areas.forEach(a => {
                        if (a.id === aa.id) {
                            staff.push(st);
                        }
                    });
                });
            });

            return staff;
        },

        /**
         * Returns array of uniq call ids
         * @return {[*, *]}
         */
        callIdsOptions() {
            let options = uniqBy(this.selectedStaff, 'value').map(call => ({
                value: call.value,
                name: call.value,
            }));
            return orderBy(options, 'name');
        },

        /**
         * Returns array of assignment area options ordered by name
         * @return {Object[]} array of assignment area objects for multi-select
         */
        aAreasOptions() {
            let options = this.aAreas.length
                ? this.aAreas.map(item => ({ value: item.id, name: item.name }))
                : [];
            return sortLocations(options);
        },

        /**
         * Returns array of selected assignment areas
         * @return {Object[]} array of full assignment area objects
         */
        selectedAA() {
            return getSelectedAAreas(this.reportData, this.aAreas);
        },

        /**
         * Returns array of location options ordered by name
         * @return {Object[]} array of location objects for multi-select
         */
        locationsOptions() {
            let options = [];
            this.selectedAA.forEach(aArea => {
                options = options.concat(
                    aArea.locations.map(item => ({
                        value: item.id,
                        name: item.name,
                    }))
                );
            });
            return sortLocations(options);
        },

        /**
         * Returns array of call type options
         * @return {Object[]} array of call type objects for multi-select
         */
        callTypesOptions() {
            return this.callTypes.length
                ? this.callTypes.map(item => ({
                      value: item.id,
                      name: item.name,
                  }))
                : [];
        },

        /**
         * Returns array of names for validation of uniqueness
         * @return {String[]} array of forbidden report names
         */
        getForbiddenReportNames() {
            return this.mode === 'add' ? [] : this.customReports.filter(report => report.id !== this.id);
        },

        /**
         * Checks whether or not form for configuration is valid
         * @returns {boolean}
         */
        isFormValid() {
            let valid = !this.$v.reportData.name.$invalid && !this.$v.reportData.scheduleValid.$invalid;
            if (this.reportData.type === REPORT_TYPES.ASCOM_EVENT) {
                valid = valid && !isEmpty(this.reportData.selectedEvent);
            }
            if (this.reportData.type === REPORT_TYPES.STAFF_PERFORMANCE) {
                valid = valid && !this.$v.reportData.callId.$invalid;
            }
            return valid;
        },
    },

    watch: {
        $route($route) {
            if ($route.name === ROUTE_NAMES.REPORTS__CUSTOM__EDIT) {
                this.custom = 'custom';
                this.setChangeWatcher();
            } else {
                this.custom = null;
                this.unsetChangeWatcher();
            }
            this.resetReportDataObj();
            this.prepareReportFormMode(this.id, this.mode);
            this.setButtons(this.mode);
        },

        selectedStaff(list) {
            if (list.length === 0) {
                this.reportData.callId = '';
            }
        },

        '$route.path'() {
            // Hide drop down in each Multiselect when going back/forward in history
            ['assignmentAreasMultiselect', 'locationsMultiselect', 'callTypesMultiselect'].forEach(ref =>
                invoke(this.$refs, ref + '.deactivate')
            );
        },
    },

    created() {
        this.prepareReportFormMode(this.id, this.mode);
        this.setButtons(this.mode);
        if (this.mode === 'edit') {
            this.custom = 'custom';
            this.setChangeWatcher();
        }
    },

    beforeDestroy() {
        if (this.unwatcher) {
            this.unwatcher();
        }
    },

    methods: {
        ...mapMutations({ setSelectedReportType: 'SET_SELECTED_REPORT_TYPE' }),

        errorDisplay,

        /**
         * Configures Show button on Report page
         */
        setShowButton() {
            this.configButton({
                buttonName: 'showButton',
                text: 'reports.show',
                action: () => {
                    if (this.isFormValid) {
                        let currentIsChanged = this.unsavedChanges;
                        this.setUnsavedChanges(false);
                        this.$router.push({
                            name: ROUTE_NAMES.REPORTS__SHOW,
                            // current unsavedChanges status has higher priority then status from url query
                            query: {
                                ...this.reportQuery(this.reportData),
                                isChanged: currentIsChanged,
                            },
                            params: this.mode === 'edit' ? { custom: 'custom' } : {},
                        });
                    }
                },
                disabled: () => {
                    let noAscomEventsToShow =
                        this.custom === 'custom' &&
                        this.reportData.type === REPORT_TYPES.ASCOM_EVENT &&
                        Object.keys(this.reportData.selectedEvent).length === 0;
                    return !this.isFormValid || this.reportData.exportDate.invalid || noAscomEventsToShow;
                },
            });
        },

        /**
         * Configures Save button on Report page
         */
        setSaveButton() {
            this.configButton({
                buttonName: 'saveButton',
                text: 'common.save',
                action: () => {
                    this.reportData.name = this.reportData.name.trim();
                    this.prepareAndSave(this.reportQuery(this.reportData));
                },
                disabled: () =>
                    !this.isFormValid || this.reportData.exportDate.invalid || !this.unsavedChanges,
            });
        },

        /**
         * Sets buttons on Report page depending on mode
         * @param {String} mode - "add" or "edit" report mode
         */
        setButtons(mode) {
            this.setShowButton();
            if (mode === 'edit') {
                this.setSaveButton();
            }
        },

        setChangeWatcher() {
            if (this.unwatcher) {
                this.unwatcher();
            }
            this.unwatcher = this.$watch('reportData', () => this.setUnsavedChanges(true), {
                deep: true,
            });
        },

        unsetChangeWatcher() {
            if (typeof this.unwatcher === 'function') {
                this.unwatcher();
                this.unwatcher = null;
                this.setUnsavedChanges(false);
            }
        },

        /**
         * Sets selected assignment areas, updates array of selected locations according to selected assignment areas,
         */
        updateAAandLocations(value) {
            this.reportData.aa = value;

            let locations = [];
            this.selectedAA.forEach(aArea => {
                locations = locations.concat(
                    this.reportData.locations.filter(loc => aArea.locations.some(l => l.id === loc.value))
                );
            });
            this.reportData.locations = locations;
        },

        /**
         * Creates report data object from source
         * @param {Object} source object of report configuration
         * @returns {}
         * id: (number),
         * name: (string),
         * type: (number),
         * typeName: (string),
         * aa: (object[]),
         * loc: (object[]),
         * ct: (object[]),
         * startTime: (object),
         * endTime: (object),
         * event: (int),
         * pairEvent: (int),
         * callType: (string),
         * callId: (string),
         * schedule: (Object)
         * }}
         */
        createReportData(source) {
            let reportData = {
                id: source.id,
                name: source.name,
                type: Number(source.type),
                typeName: source.typeName,
                aa: this.isAllDataInArray(this.aAreasOptions, source.aa)
                    ? []
                    : this.aAreasOptions.filter(area => (source.aa || []).includes(area.name)),
                locations: this.collapseLocations(
                    this.locationsOptions.filter(loc => (source.loc || []).includes(loc.name))
                ),
                callTypes: this.isAllDataInArray(this.callTypesOptions, source.ct)
                    ? []
                    : this.callTypesOptions.filter(type => (source.ct || []).includes(type.value)),
                exportDate: {
                    from: {
                        date: source.startDate,
                    },
                    to: {
                        date: source.endDate,
                    },
                    invalid: false,
                },
                callType: Number(source.callType),
                callId: this.staff.find(st => st.value === source.call_id),
                scheduleValid: true,
                schedule: {
                    scheduled: source.schedule_type !== null,
                    scheduleType: Number(source.schedule_type),
                    emailingTime:
                        source.schedule_type !== null && source.emailing_time
                            ? source.emailing_time.substring(0, 5)
                            : '00:00',
                    scheduleEmails: source.schedule_type !== null ? source.schedule_emails : [],
                    scheduleDescription: source.schedule_type !== null ? source.schedule_description : '',
                    scheduleLastRun: source.schedule_type === 14 ? source.schedule_last_run : null,
                },
            };
            if (reportData.type === REPORT_TYPES.ASCOM_EVENT) {
                reportData.selectedEvent = {
                    calltrace_id: Number(source.calltrace_id),
                    types: source.event_types,
                    location: source.event_location,
                    start: source.start,
                    end: source.end,
                    incomplete: source.incomplete,
                };
            }

            if (reportData.type === REPORT_TYPES.PAIR_EVENT) {
                reportData.pairEvent = Number(source.pairEvent);
            }

            if (reportData.type === REPORT_TYPES.STAFF_PERFORMANCE) {
                reportData.callId = source.call_id;
            }

            return reportData;
        },

        /**
         * Collapses all selected locations to empty array ('All')
         * @param {Array} locations of report
         * @returns {Array} selected locations or empty array if all locations selected
         */
        collapseLocations(locations) {
            return this.isAllDataInArray(
                this.locationsOptions,
                locations.map(loc => loc.name)
            )
                ? []
                : locations;
        },

        /**
         * Prepares configuration for report form according to mode for configuration
         * @param {Number} id of configured report
         * @param {String} mode - "add" or "edit" report mode
         */
        prepareReportFormMode(id, mode) {
            if (!isEmpty(this.queryData)) {
                this.convertQueryToProps();
                this.reportData = this.createReportData(this.queryData);
                this.$nextTick(() => {
                    this.setUnsavedChanges(
                        typeof this.queryData.isChanged === 'string'
                            ? this.queryData.isChanged === 'true'
                            : this.queryData.isChanged
                    );
                });
            } else if (mode === 'add') {
                this.reportData.id = id;
                this.reportData.type = id;
                this.reportData.name = (this.defaultReports.find(item => item.type === id) || []).name;
                this.$nextTick(() => {
                    this.setUnsavedChanges(false);
                });
            } else if (mode === 'edit') {
                let customReport = this.customReports.find(item => item.id === id);
                if (customReport) {
                    this.reportData = this.createReportData(this.transformReportFromDB(customReport));
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                    });
                } else {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t('reports.notFound'),
                    });
                    errorHandler(404, ROUTE_NAMES.REPORTS);
                }
            }
            this.setSelectedReportType(this.reportData.type);
        },

        /**
         * Maps report configuration from DB on report object for Report page
         * @param {Object} customReport - object from DB
         * @returns {Object} transformed report object
         */
        transformReportFromDB(customReport) {
            return transform(
                customReport,
                (result, value, key) => {
                    if (key === 'configuration') {
                        result.aa = value.aa;
                        result.loc = value.locations;
                        result.ct = value.call_types;
                        result.startDate = value.startDate;
                        result.endDate = value.endDate;
                        result.pairEvent = value.pair_type;
                        result.callType = value.call_type;
                        if (result.type === REPORT_TYPES.ASCOM_EVENT) {
                            result.calltrace_id = value.selectedEvent.calltrace_id;
                            result.event_types = value.selectedEvent.types;
                            result.event_location = value.selectedEvent.location;
                            result.start = value.selectedEvent.start;
                            result.end = value.selectedEvent.end;
                            result.incomplete = value.selectedEvent.incomplete;
                        }
                        if (result.type === REPORT_TYPES.STAFF_PERFORMANCE) {
                            result.call_id = value.call_id;
                        }
                    } else {
                        result[key] = value;
                    }
                },
                {}
            );
        },

        prepareAndSave(reportData) {
            let report = {
                reportName: reportData.name,
                emailingTime:
                    reportData.emailing_time !== null && reportData.emailing_time
                        ? reportData.emailing_time
                        : '00:00',
                scheduleEmails: reportData.schedule_type !== null ? reportData.schedule_emails : [],
                scheduleType: reportData.schedule_type,
                scheduleDescription:
                    reportData.schedule_type !== null ? reportData.schedule_description : null,
                scheduleLastRun: reportData.schedule_type === 14 ? reportData.schedule_last_run : null,
            };
            this.save(report, reportData);
        },

        saveReport(report, reportData) {
            return saveReport(
                reportData.id,
                getCustomReportObject({ ...reportData, ...report }, this.aAreas, this.callTypes)
            )
                .then(() => {
                    if (this.$router.currentRoute.name === ROUTE_NAMES.REPORTS__CUSTOM__EDIT) {
                        this.showLoading(false);
                        this.setUnsavedChanges(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });

                        this.$emit('refresh');
                        this.$nextTick(() => {
                            // Remove all props in url query
                            this.$router.replace({
                                name: this.$route.name,
                                params: { id: this.reportData.id },
                            });
                        });
                    }
                })
                .catch(response => {
                    this.showLoading(false);
                    if (response.data && response.data.error) {
                        this.popup({
                            type: ERROR,
                            header: this.$t('common.error'),
                            text: this.$t(response.data.error),
                        });
                    }
                    this.handleError(response.status);
                });
        },

        save(report, reportData) {
            this.showLoading(true);
            if (this.userType === USER_TYPES.SUPER_USER) {
                resourceAssignmentAreas()
                    .then(response => {
                        if (response.data) {
                            if (response.data[0].id !== this.userAreaId) {
                                this.showLoading(false);
                                this.popup({
                                    type: ERROR,
                                    header: this.$t('common.error'),
                                    text: this.$t('error.common.forbidden'),
                                });
                                return Promise.reject(response);
                            }
                            return this.saveReport(report, reportData);
                        }
                    })
                    .catch(error => {
                        this.showLoading(false);
                        if (error.status !== 200) {
                            this.popup({
                                type: ERROR,
                                header: this.$t('common.error'),
                                text: this.$t('error.common.forbidden'),
                            });
                        }
                    });
            } else {
                return this.saveReport(report, reportData);
            }
        },

        /**
         * Handles api request error depending on status
         * @param status
         */
        handleError(status) {
            if (Number(status) === 404) {
                this.$emit('refresh');
                this.$nextTick(() => {
                    this.showLoading(false);
                    this.setUnsavedChanges(false);
                    this.$nextTick(() => {
                        this.$router.replace({ name: ROUTE_NAMES.REPORTS });
                    });
                });
            } else if (status === 409) {
                this.$emit('refresh');
            }
        },

        /**
         * Converts data of query for assignment areas, location and call types to array             *
         */
        convertQueryToProps() {
            this.queryData.aa = toArray(this.queryData.aa);
            this.queryData.loc = toArray(this.queryData.loc);
            this.queryData.ct = toNumberArray(this.queryData.ct);
            this.queryData.schedule_emails = toArray(this.queryData.schedule_emails);
        },

        /**
         * Forms report data object to pass it via query
         * @param {Object} source object of report configuration
         * @returns {{
         * id: (Object|ObjectConstructor|*|number),
         * name: (*|string),
         * type: (Object|ObjectConstructor|*|number),
         * typeName: (*|string),
         * aa: (any[] | Array),
         * loc: (any[] | Array),
         * ct: (any[] | Array),
         * startDate: (*|computed.startDate|computed.timeFrom|string|timeFrom|string),
         * endDate: (*|computed.timeTo|string|timeTo|string),
         * }}
         */
        reportQuery(source) {
            let startDate = source.exportDate.from.date ? source.exportDate.from.date : null;
            let endDate = source.exportDate.to.date ? source.exportDate.to.date : null;
            startDate = startDate && typeof startDate === 'object' ? startDate.getTime() : startDate;
            endDate = endDate && typeof endDate === 'object' ? endDate.getTime() : endDate;
            let query = {
                id: source.id ? source.id : source.type,
                name: source.name.trim(),
                type: source.type,
                typeName: source.typeName,
                aa: source.aa.map(a => a.name),
                loc: source.locations.map(loc => loc.name),
                ct: source.callTypes.map(type => type.value),
                startDate: startDate,
                endDate: endDate,
            };
            if (this.mode === 'edit') {
                query.schedule_type = source.schedule.scheduled ? source.schedule.scheduleType : null;
                query.emailing_time = source.schedule.scheduled ? source.schedule.emailingTime : '00:00';
                query.schedule_emails = source.schedule.scheduled ? source.schedule.scheduleEmails : [];
                query.schedule_description = source.schedule.scheduled
                    ? source.schedule.scheduleDescription
                    : '';
                if (source.schedule.scheduleType === 14) {
                    query.schedule_last_run = source.schedule.scheduleLastRun;
                }
            }
            if (source.type === REPORT_TYPES.ASCOM_EVENT) {
                query.calltrace_id = source.selectedEvent.calltrace_id;
                query.event_types = source.selectedEvent.types;
                query.event_location = source.selectedEvent.location;
                query.start_time = source.selectedEvent.start;
                query.end_time = source.selectedEvent.end;
                query.start = source.selectedEvent.start;
                query.end = source.selectedEvent.end;
                query.incomplete = source.selectedEvent.incomplete;
            }

            if (source.type === REPORT_TYPES.PAIR_EVENT) {
                query.pairEvent = source.pairEvent || 6;
            }

            if (source.type === REPORT_TYPES.EXCEPTION) {
                query.callType = source.callType || 1;
            }

            if (source.type === REPORT_TYPES.STAFF_PERFORMANCE) {
                query.call_id = source.callId;
                query.call_id_names = this.callIdNames(source.callId);
            }
            return query;
        },

        /**
         * Return list of names for selected call id
         * @param {string} callId
         * @return {string}
         */
        callIdNames(callId) {
            let names = [];
            this.selectedStaff.forEach(staff => {
                if (staff.value === callId) {
                    names.push(staff.name);
                }
            });

            return orderBy(names).join(', ');
        },

        /**
         * Resets report data object to default
         */
        resetReportDataObj() {
            this.reportData = Object.assign({}, this.reportData, {
                name: '',
                type: 0,
                typeName: '',
                aa: [],
                locations: [],
                callTypes: [],
                exportDate: {
                    from: {
                        date: currentDate,
                    },
                    to: {
                        date: nextDate,
                    },
                    invalid: false,
                },
                pairEvent: 6,
                callType: 1,
                callId: '',
                selectedEvent: {},
                schedule: {
                    scheduled: false,
                    scheduleType: 7,
                    scheduleEmails: this.defaultRecipients,
                    scheduleDescription: '',
                    scheduleLastRun: null,
                },
                scheduleValid: true,
            });
            this.$nextTick(() => {
                this.$refs.DateTimePickerForm.updateDateFields();
            });
        },

        /**
         * Deletes custom report entry from database by id and sends event to refresh custom report list
         * @param {Number} id
         * @return {Promise}
         */
        deleteReportById(id) {
            return resourceCustomReport({ method: 'delete', id })
                .then(() => {
                    // Anyway we wait parent's ajax, then wait reset unsavedChanges in vuex and after then go to another route
                    if (this.$router.currentRoute.name === ROUTE_NAMES.REPORTS__CUSTOM__EDIT) {
                        this.$emit('refresh');
                        this.$nextTick(() => {
                            this.showLoading(false);
                            this.setUnsavedChanges(false);
                            this.$nextTick(() => {
                                this.$router.replace({ name: ROUTE_NAMES.REPORTS });
                            });
                        });
                    }
                })
                .catch(response => {
                    this.$nextTick(() => {
                        this.showLoading(false);
                        if (response.data && response.data.error) {
                            this.popup({
                                type: ERROR,
                                header: this.$t('common.error'),
                                text: this.$t(response.data.error),
                            });
                        }

                        if (response.status === 404) {
                            this.$emit('refresh');
                            this.setUnsavedChanges(false);
                            errorHandler(response.status, ROUTE_NAMES.REPORTS);
                        }
                    });
                });
        },

        deleteReport(id) {
            let deletedCustomReport = this.customReports.find(item => item.id === id);
            return this.confirmation({
                confirmTitle: this.$t('reports.customReports.removingCustomReportTitle'),
                confirmBody: this.$t('reports.customReports.removingCustomReport', {
                    name: deletedCustomReport ? deletedCustomReport.name : '',
                }),
            }).then(r => {
                if (r) {
                    this.showLoading(true);
                    if (this.userType === USER_TYPES.SUPER_USER) {
                        resourceAssignmentAreas()
                            .then(response => {
                                if (response.data) {
                                    if (response.data[0].id !== this.userAreaId) {
                                        this.showLoading(false);
                                        this.popup({
                                            type: ERROR,
                                            header: this.$t('common.error'),
                                            text: this.$t('error.common.forbidden'),
                                        });
                                        return Promise.reject(response);
                                    }
                                    return this.deleteReportById(id);
                                }
                            })
                            .catch(error => {
                                this.showLoading(false);
                                if (error.status !== 200) {
                                    this.popup({
                                        type: ERROR,
                                        header: this.$t('common.error'),
                                        text: this.$t('error.common.forbidden'),
                                    });
                                }
                            });
                    } else {
                        return this.deleteReportById(id);
                    }
                }
            });
        },

        /**
         * Updates schedule field in reportData
         * if value.field =  schedule, it updates hole schedule object
         * else it updates [value.field] prop of schedule
         * value.valid need to validate scheduler component
         * @param value {Object} with field, value and valid propierties             *
         */
        updateSchedule(value) {
            if (value.field === 'scheduled' && value.value && !this.reportData.schedule.scheduleType) {
                let defSchedule = {
                    scheduled: true,
                    scheduleType: 7,
                    emailingTime: '00:00',
                    scheduleEmails: this.defaultRecipients,
                    scheduleDescription: this.$t('reports.defaultReportEmailText', {
                        reportName: this.reportData.name,
                    }),
                };
                this.reportData.schedule = defSchedule;
            } else if (value.field !== 'schedule') {
                this.reportData.schedule[value.field] = value.value;
            }
            this.reportData.scheduleValid = value.valid;
        },
    },
};
</script>
